"use client";
import React from "react";
import { NodeModel } from "@minoru/react-dnd-treeview";
import { WcStack, WcTypography } from "@bpipartners/webui";
import { useTheme } from "@guberno/utils/theme";
import { useTranslations } from "next-intl";
import { DRAWER_ICONS, LEGACY_DRAWER_ICONS } from "./DrawerIconRegistry";
import { MY_CIRCLES_DYNAMIC_PARENT_ID } from "./constants";
import { MdOutlineKeyboardArrowDown, MdOutlineKeyboardArrowUp } from "react-icons/md";

/**
 * Module-level so React never sees a component "created during render": the
 * lookup itself is a direct object access, which the compiler can prove stable.
 */
const DrawerIconView = ({ icon, menuName }: { icon?: string | null; menuName: string }) => {
  const Icon = DRAWER_ICONS[icon ?? ""] ?? LEGACY_DRAWER_ICONS[menuName] ?? null;
  return Icon ? <Icon size={20} /> : null;
};

interface CustomDrawerNodeProps {
  node: NodeModel;
  depth: number;
  isSelected: boolean;
  hasChild: boolean;
  isChildItem: boolean;
  isParentOfSelectedChild: boolean;
  onToggle: (id: string | number) => void;
  isOpen: boolean;
  onSelect: (node: NodeModel) => void;
  isDrawerExpaned: boolean;
}

export const CustomDrawerNode = ({
  onToggle,
  depth,
  isOpen,
  node,
  onSelect,
  isSelected,
  hasChild,
  isChildItem = false,
  isParentOfSelectedChild,
  isDrawerExpaned,
}: CustomDrawerNodeProps) => {
  const { id, data } = node;
  //@ts-ignore — node.data is untyped in the dnd-tree model
  const menuIcon = data?.icon as string | undefined;

  const t = useTranslations("common");
  const theme = useTheme();
  const handleToggle = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onToggle(node.id);
  };

  const handleSelect = () => onSelect(node);

  return (
    <WcStack
      id={`CustomDrawerNode${id}`}
      direction={"row"}
      sx={{
        // Children are indented 20px via marginLeft — shrink the width to match so the
        // right edge (and its rounded corners) stays inside the drawer like the main items.
        width: isChildItem ? "calc(100% - 20px)" : "100%",
        justifyContent: isDrawerExpaned ? "flex-start" : "center",
        borderRadius: 2,
        padding: 4,
        alignItems: "center",
        cursor: "pointer",
        boxShadow: (isSelected && !isChildItem) || isParentOfSelectedChild ? `inset 4px 0 0 ${theme.appColors.primary}` : "none",
        // Selected main items and the parent of a selected child keep the grey background + left
        // accent; a selected child now gets the same grey background (text color was its only cue).
        backgroundColor: isSelected || isParentOfSelectedChild ? theme.appColors.surfaceContainer : "inherit",
        // Main items and children share the same light-grey hover.
        "&:hover": {
          background: theme.appColors.surfaceContainerLow,
        },
      }}
      height={theme.dimensions.appDrawerItemHeight}
      marginLeft={isChildItem ? "20px" : 0}
    >
      <WcStack direction={"row"} width={"100%"} alignItems={"center"} justifyContent={isDrawerExpaned ? "inherit" : "center"}>
        <WcStack
          width={isDrawerExpaned ? "100%" : "auto"}
          spacing={5}
          direction={"row"}
          alignItems={"center"}
          onClick={(event) => {
            //@ts-ignore
            if (data.type !== "group") {
              handleSelect();
            } else {
              handleToggle(event);
            }
          }}
          sx={{
            color: isSelected ? theme.appColors.primary : theme.appPalette.secondary[30],
            "&:hover": {
              color: theme.appPalette.secondary[10],
            },
          }}
        >
          <WcStack paddingLeft={isDrawerExpaned ? 0 : 2}>
            <DrawerIconView icon={menuIcon} menuName={node.text} />
          </WcStack>
          {isDrawerExpaned && (
            <WcTypography variant={isChildItem ? "body2" : "body1"}>
              {/* Children of the dynamic My Circles group carry literal names — no translation. */}
              {node.parent === MY_CIRCLES_DYNAMIC_PARENT_ID ? node.text : t(node.text)}
            </WcTypography>
          )}
        </WcStack>
      </WcStack>

      {hasChild &&
        isDrawerExpaned &&
        (isOpen ? (
          <WcStack onClick={handleToggle}>
            <MdOutlineKeyboardArrowUp />
          </WcStack>
        ) : (
          <WcStack onClick={handleToggle}>
            <MdOutlineKeyboardArrowDown />
          </WcStack>
        ))}
    </WcStack>
  );
};
